import { AreaCode } from '@ebalance/proto/bfftypes/v1/area_pb'
import i18next from 'i18next'

/**
 * AreaCode → 日本語ラベル (canonical)。
 *
 * ラベル文言は `common:area.*` に集約する (= 旧来 balanceGeneration / adminLossRate 等の
 * page namespace に同一ラベルが重複定義されていたものの SoT)。 エリア名を扱う画面は
 * 本関数を経由してラベルを得る。
 */
export function convertAreaLabel(value: AreaCode): string {
  const t = i18next.t.bind(i18next)
  switch (value) {
    case AreaCode.HOKKAIDO:
      return t('common:area.hokkaido')
    case AreaCode.TOHOKU:
      return t('common:area.tohoku')
    case AreaCode.TOKYO:
      return t('common:area.tokyo')
    case AreaCode.CHUBU:
      return t('common:area.chubu')
    case AreaCode.HOKURIKU:
      return t('common:area.hokuriku')
    case AreaCode.KANSAI:
      return t('common:area.kansai')
    case AreaCode.CHUGOKU:
      return t('common:area.chugoku')
    case AreaCode.SHIKOKU:
      return t('common:area.shikoku')
    case AreaCode.KYUSHU:
      return t('common:area.kyushu')
    case AreaCode.OKINAWA:
      return t('common:area.okinawa')
    default:
      return ''
  }
}

/**
 * AreaCode → OCCTO 5 桁コードの末尾 1 桁 (= エリアコード番号)。
 *
 * 北海道〜九州 = `'1'`〜`'9'`、 沖縄 = `'0'` (= `AreaCode.OKINAWA` の enum 値は 10 だが
 * OCCTO 末尾桁は `'0'`)。 OCCTO 5 桁コード (BG コード / 計画提出者コード等) の末尾 1 文字
 * がこの値になる (backend `valueobject.AreaCode.OcctoSuffix` と対応)。
 * UNSPECIFIED や未定義値は `undefined` を返す。
 *
 * 注: `String(areaCode)` で代用してはいけない (= 沖縄が `'10'` (2 桁) になり、 末尾 `'0'` の
 * 沖縄 BG コードに `endsWith` でマッチしない)。
 */
export function occtoAreaDigit(value: AreaCode): string | undefined {
  switch (value) {
    case AreaCode.HOKKAIDO:
      return '1'
    case AreaCode.TOHOKU:
      return '2'
    case AreaCode.TOKYO:
      return '3'
    case AreaCode.CHUBU:
      return '4'
    case AreaCode.HOKURIKU:
      return '5'
    case AreaCode.KANSAI:
      return '6'
    case AreaCode.CHUGOKU:
      return '7'
    case AreaCode.SHIKOKU:
      return '8'
    case AreaCode.KYUSHU:
      return '9'
    case AreaCode.OKINAWA:
      return '0'
    default:
      return undefined
  }
}

/**
 * エリア選択 UI で表示する AreaCode の並び (北→南)。 UNSPECIFIED を除く実エリア 10 個。
 * OCCTO マスタ各 form の area select で共通利用する (= 旧来は各 FormFields が同じ配列を
 * 重複定義していた)。 全エリアを扱う文脈はこちらを使う。
 */
export const AREA_CODE_OPTIONS: AreaCode[] = [
  AreaCode.HOKKAIDO,
  AreaCode.TOHOKU,
  AreaCode.TOKYO,
  AreaCode.CHUBU,
  AreaCode.HOKURIKU,
  AreaCode.KANSAI,
  AreaCode.CHUGOKU,
  AreaCode.SHIKOKU,
  AreaCode.KYUSHU,
  AreaCode.OKINAWA,
]

/**
 * JEPX (スポット / 時間前) 取引対象のエリア (= 沖縄を除く 9 エリア、 北→南)。
 *
 * 沖縄は本土と連系しておらず JEPX 対象外 (= `mock-app/docs/domain-knowledge.md` 時間前市場
 * 「自エリア (単独 9 エリア: 北海道〜九州、 沖縄除外)」/ スポット「9 エリアプライス」)。
 * 入札系画面のエリア選択は全 10 エリア (= [[AREA_CODE_OPTIONS]]) ではなく、 この 9 エリアを
 * SoT とする (= 旧来は各画面が同じ 9 エリア配列をハードコードで重複定義していた)。
 */
export const JEPX_AREA_CODES: AreaCode[] = [
  AreaCode.HOKKAIDO,
  AreaCode.TOHOKU,
  AreaCode.TOKYO,
  AreaCode.CHUBU,
  AreaCode.HOKURIKU,
  AreaCode.KANSAI,
  AreaCode.CHUGOKU,
  AreaCode.SHIKOKU,
  AreaCode.KYUSHU,
]

const parseStoredAreaCodes = (
  raw: string,
  candidates: readonly AreaCode[],
): AreaCode[] | undefined => {
  try {
    const parsed: unknown = JSON.parse(raw)
    if (!Array.isArray(parsed)) return undefined
    const stored: readonly unknown[] = parsed
    return candidates.filter((code) => stored.includes(code))
  } catch {
    return undefined
  }
}

/** 保存した JSON (エリアコードの配列) を JEPX 取引対象の 9 エリアに絞って読む。 配列でない・壊れた値は undefined。 */
export const parseJepxAreaCodes = (raw: string): AreaCode[] | undefined =>
  parseStoredAreaCodes(raw, JEPX_AREA_CODES)

/** 保存した JSON (エリアコードの配列) を実エリア 10 個に絞って読む。 配列でない・壊れた値は undefined。 */
export const parseAreaCodes = (raw: string): AreaCode[] | undefined =>
  parseStoredAreaCodes(raw, AREA_CODE_OPTIONS)

/**
 * Returns whether JEPX (スポット / 時間前) can be traded in the given area.
 * [[JEPX_AREA_CODES]] に含まれるかだけを答える。
 */
export function isJepxArea(areaCode: AreaCode): boolean {
  return JEPX_AREA_CODES.includes(areaCode)
}

/**
 * Returns whether two areas belong to the same counterparty interchange
 * group. Okinawa is electrically isolated; every other defined area belongs
 * to the mainland group.
 */
export function isSameCounterpartyAreaGroup(
  selfAreaCode: AreaCode,
  candidateAreaCode: AreaCode,
): boolean {
  return (
    (selfAreaCode === AreaCode.OKINAWA) ===
    (candidateAreaCode === AreaCode.OKINAWA)
  )
}
